import { FC, useEffect, useState, useRef } from 'react';
import { repeat } from 'rxjs';
import { Stack } from '@mui/material';
import {
  ImportDetailsInfo,
  IValidationData,
  IOptionsData,
  defOptionsData,
  defValidationData,
} from './components/importDetailsInfo.component.tsx';
import { AddImportInfo } from './components/addImportInfo.component.tsx';
import { configurationTabLoadingService } from '../../../configurationTabLoading.service.ts';
import { ExtendedListImportDataFromFileQueryRes } from '../../../services/configuration.service.ts';
import { IUploadFileToS3Return } from '../../../../../../shared/components/fileUpload/fileUpload.component.tsx';
import {
  configurationService,
  ImportTabOptionsRes,
  isImportFailureMessageKey,
} from '../../../services/configuration.service.ts';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler.ts';
import { useTranslation } from 'react-i18next';
import {
  C_Ca_Visibility_Status,
  C_Correspondence_Language,
  C_Customer_Address_Kind,
  C_Customer_Def_Order_Source,
  C_Debitor_Status,
  C_Direct_Debit_Banks,
  C_Discounts_And_Prices_Kind,
  C_Discount_Kind,
  C_E_Invoice_Format,
  C_Invoice_Interval,
  C_Procurement_Product_Kind,
  C_Report,
  C_Sale_Unit,
  C_Tax_System,
  C_Vat_Code,
} from '../../../../../../graphql/generatedModel.ts';
import { Scalars } from '../../../../../../graphql/generatedModel.ts';
import {
  ExtendedArticleImportDataFromFile,
  ExtendedArticleSpecialPriceImportDataFromFile,
  ExtendedCustomerImportDataFromFile,
} from '../../../services/configuration.service.ts';
import { LoadingType } from '../../../../../../shared/services/loading/loading.service.ts';
import { decodeEnumLabel } from './helpers/decodeEnumLabel.ts';
import { initEnumOptions, initOptionsArray } from './helpers/importOptions.ts';
import { heuristicSaleUnitDetection } from './helpers/heuristicSaleUnitDetection.ts';
import { heuristicVATDetection } from './helpers/heuristicVATDetection.ts';
import { resolveSpecialPriceCategories } from './helpers/resolveSpecialPriceCategories.ts';

export const ImportTab: FC = () => {
  const { t } = useTranslation();
  const [importData, setImportData] = useState<IImportData>();
  const [validationData, setValidationData] = useState<IValidationData>(defValidationData);
  // The enum-backed lists are available before any request is made, so the
  // preview never renders a recognised enum value as an empty cell while the
  // dictionary fetch is still in flight — or has failed.
  const [optionsData, setOptionsData] = useState<IOptionsData>(() => ({
    ...defOptionsData,
    ...initEnumOptions(t),
  }));
  const [currentFileName, setCurrentFileName] = useState<string>('');
  const optionsDataRef = useRef<IOptionsData>(optionsData);
  const validationDataRef = useRef<IValidationData>(defValidationData);

  useEffect(() => {
    optionsDataRef.current = optionsData;
  }, [optionsData]);

  useEffect(() => {
    validationDataRef.current = validationData;
  }, [validationData]);

  useEffect(() => {
    const optionsSub = configurationService.sub
      .importTabOptions()
      .pipe(responseHandler<ImportTabOptionsRes>({ errorReturnType: defOptionsData }))
      .subscribe((data) => setOptionsData(initOptionsArray(data, t)));

    configurationService.pub.importTabOptions();

    const validationDataSub = configurationService.sub
      .listValidationDataForImport()
      .pipe(responseHandler<IValidationData>({ errorReturnType: defValidationData }))
      .subscribe((data) => {
        setValidationData(data);
      });

    configurationService.pub.listValidationDataForImport();

    const importDataSub = configurationService.sub
      .getImportDataFromFile()
      .pipe(
        responseHandler<ExtendedListImportDataFromFileQueryRes>({
          errorReturnType: {
            articleImportData: [],
            customerImportData: [],
            articleSpecialPriceImportData: [],
            columnMapping: [],
          },
          // Without this the failure text is thrown away and replaced by the
          // generic "something went wrong", which is precisely what an operator
          // whose file is missing its customer-number column must not be told.
          customErrorHandler: (err) =>
            isImportFailureMessageKey(err.message)
              ? { message: err.message, reportToSentry: false }
              : undefined,
        }),
        // A failed file must not end the subscription. `responseHandler` turns
        // the error into a value and then completes the stream, which left the
        // tab subscribed to nothing: the next upload published its action into a
        // stream no one was listening to, so no request was ever sent and the
        // spinner ran forever. Re-subscribing is safe because the service clears
        // the replayed action in its `finalize` before this runs (WA-3846).
        repeat(),
      )
      .subscribe((data) => {
        setImportData(
          preprocessImportData(data, optionsDataRef.current, validationDataRef.current),
        );
        configurationTabLoadingService.pub.loading(false);
      });

    return () => {
      importDataSub.unsubscribe();
      validationDataSub.unsubscribe();
      optionsSub.unsubscribe();
    };
  }, []);

  const handleOnFileUploaded: (result: IUploadFileToS3Return) => void = ({ files: [file] }) => {
    const { path } = file;
    configurationService.pub.getImportDataFromFile({ key: path });
    setCurrentFileName(path.split(/(\\|\/)/g).pop() || '');
  };

  const loadingHandler = () => {
    setImportData({
      articleImportData: [],
      customerImportData: [],
      articleSpecialPriceImportData: [],
      columnMapping: [],
    });
    configurationTabLoadingService.pub.loading({ isLoading: true, type: LoadingType.LONG });
  };

  return (
    <Stack
      sx={{
        height: '100%',
      }}
    >
      {importData ? (
        <ImportDetailsInfo
          data={importData}
          validationData={validationData}
          optionsData={optionsData}
          onFileUploaded={handleOnFileUploaded}
          onLoading={loadingHandler}
          currentFielName={currentFileName}
        />
      ) : (
        <AddImportInfo onFileUploaded={handleOnFileUploaded} onLoading={loadingHandler} />
      )}
    </Stack>
  );
};

// Anyway need to do it here because of translations which are stored on frontend...
// Also here we will truncete too long strings for showing
// to the customer real value which will be saved in db
const preprocessImportData = (
  data: ExtendedListImportDataFromFileQueryRes,
  optionsData: IOptionsData,
  validationData: IValidationData,
): IImportData => {
  const result: IImportData = {
    customerImportData: [],
    articleImportData: [],
    columnMapping: data.columnMapping ?? [],
    articleSpecialPriceImportData: data.articleSpecialPriceImportData.map((value) =>
      resolveSpecialPriceCategories(value, optionsData.priceCategories),
    ),
  };

  data.customerImportData.forEach((value) =>
    result.customerImportData.push({
      ...value,
      debtorAccount: value.debtorAccount ?? null,
      slipReferenceNo: value.slipReferenceNo?.slice(0, 32),
      bank1Name: value.bank1Name?.slice(0, 32),
      bank1BIN: value.bank1BIN?.slice(0, 16),
      bank1AccountNo: value.bank1AccountNo?.slice(0, 32),
      bank1IBAN: value.bank1IBAN?.slice(0, 32),
      bank1BIC: value.bank1BIC?.slice(0, 32),
      bank2Name: value.bank2Name?.slice(0, 32),
      bank2BIN: value.bank2BIN?.slice(0, 16),
      bank2AccountNo: value.bank2AccountNo?.slice(0, 32),
      bank2IBAN: value.bank2IBAN?.slice(0, 32),
      bank2BIC: value.bank2BIC?.slice(0, 32),
      eBillAccount: value.eBillAccount?.slice(0, 40),
      salutation: value.salutation?.slice(0, 40),
      name: value.name?.slice(0, 40),
      surname: value.surname?.slice(0, 40),
      addition: value.addition?.slice(0, 40),
      street: value.street?.slice(0, 40),
      postBox: value.postBox?.slice(0, 40),
      zipCode: value.zipCode?.slice(0, 12),
      city: value.city?.slice(0, 40),
      internalName: value.internalName?.slice(0, 60),
      deliveryAddressSalutation: value.deliveryAddressSalutation?.slice(0, 40),
      deliveryAddressName: value.deliveryAddressName?.slice(0, 40),
      deliveryAddressAddition: value.deliveryAddressAddition?.slice(0, 40),
      deliveryAddressStreet: value.deliveryAddressStreet?.slice(0, 40),
      deliveryAddressPostBox: value.deliveryAddressPostBox?.slice(0, 40),
      deliveryAddressZipCode: value.deliveryAddressZipCode?.slice(0, 12),
      deliveryAddressCity: value.deliveryAddressCity?.slice(0, 40),
      phoneBusiness: value.phoneBusiness?.slice(0, 40),
      phoneBusiness2: value.phoneBusiness2?.slice(0, 40),
      phonePrivate: value.phonePrivate?.slice(0, 40),
      fax: value.fax?.slice(0, 40),
      phoneMobile: value.phoneMobile?.slice(0, 40),
      website: value.website?.slice(0, 40),
      contactPerson: value.contactPerson?.slice(0, 40),
      tourPreparationArea: value.tourPreparationArea?.slice(0, 16),
      tourSatPreparationArea: value.tourSatPreparationArea?.slice(0, 16),
      tourSunPreparationArea: value.tourSunPreparationArea?.slice(0, 16),
      besrBankId: decodeDictValue(value.besrBankId, optionsData.banks),
      caPriceAndDiscountSourceId: decodeEnumLabel(
        value.caPriceAndDiscountSourceId,
        C_Discounts_And_Prices_Kind,
      ),
      correspondenceLanguage: decodeEnumLabel(
        value.correspondenceLanguage,
        C_Correspondence_Language,
      ),
      collectiveInvoicingToCustomerId: resolveCustomerReference(
        value.collectiveInvoicingToCustomerId,
        validationData.customerIdByNo,
      ),
      collectiveInvoicingCustomerNo: value.collectiveInvoicingToCustomerId ?? null,
      customerAddressKindId: decodeEnumLabel(value.customerAddressKindId, C_Customer_Address_Kind),
      customerGroupId: decodeDictValue(value.customerGroupId, optionsData.customerGroups),
      debitorStatus: decodeEnumLabel(value.debitorStatus, C_Debitor_Status),
      defaultOrderSourceId: decodeEnumLabel(
        value.defaultOrderSourceId,
        C_Customer_Def_Order_Source,
      ),
      deliveryNoteGroupId: decodeDictValue(
        value.deliveryNoteGroupId,
        optionsData.deliveryNoteGroups,
      ),
      directDebitBankId: decodeEnumLabel(value.directDebitBankId, C_Direct_Debit_Banks),
      discountKindId: decodeEnumLabel(value.discountKindId, C_Discount_Kind),
      eInvoiceFormat: decodeEnumLabel(value.eInvoiceFormat, C_E_Invoice_Format),
      eInvoiceRoutingAddress: value.eInvoiceRoutingAddress?.slice(0, 60),
      invoicePaymentSlipReportId: decodeEnumLabel(value.invoicePaymentSlipReportId, C_Report),
      invoiceReportId: decodeEnumLabel(value.invoiceReportId, C_Report),
      invoiceSummaryReportId: decodeEnumLabel(value.invoiceSummaryReportId, C_Report),
      invoicingIntervalId: decodeEnumLabel(value.invoicingIntervalId, C_Invoice_Interval),
      offerReportId: decodeEnumLabel(value.offerReportId, C_Report),
      orderReportId: decodeEnumLabel(value.orderReportId, C_Report),
      priceCategoryId: decodeDictValue(value.priceCategoryId, optionsData.priceCategories),
      quantityDiscountSettingId: decodeDictValue(
        value.quantityDiscountSettingId,
        optionsData.quantityDiscounts,
      ),
      taxSystemId: decodeEnumLabel(value.taxSystemId, C_Tax_System),
      tourplanId: decodeDictValue(value.tourplanId, optionsData.tourplans),
      tourplanSatId: decodeDictValue(value.tourplanSatId, optionsData.tourplans),
      tourplanSunId: decodeDictValue(value.tourplanSunId, optionsData.tourplans),
      transportSectorId: decodeDictValue(value.transportSectorId, optionsData.postSectors),
    }),
  );

  data.articleImportData.forEach((value) =>
    result.articleImportData.push({
      ...value,
      description: value.description?.slice(0, 64),
      availabilityInProcurementKindId: decodeEnumLabel(
        value.availabilityInProcurementKindId,
        C_Procurement_Product_Kind,
      ),
      caTemplateId: decodeDictValue(value.caTemplateId, optionsData.caTemplates),
      caProductGroupId: decodeDictValue(value.caProductGroupId, optionsData.caProductGroups),
      caVisibilityStatusId: decodeEnumLabel(value.caVisibilityStatusId, C_Ca_Visibility_Status),
      discountGroupId: decodeDictValue(value.discountGroupId, optionsData.discountGroups),
      marketingGroupId: decodeDictValue(value.marketingGroupId, optionsData.marketingGroups),
      productionGroupId: decodeDictValue(value.productionGroupId, optionsData.productionGroups),
      saleUnitId:
        decodeEnumLabel(value.saleUnitId, C_Sale_Unit) ??
        heuristicSaleUnitDetection(value.saleUnitId ?? ''),
      shippingPointId: decodeDictValue(value.shippingPointId, optionsData.shippingPoints),
      vatCode:
        decodeEnumLabel(value.vatCode, C_Vat_Code) ?? heuristicVATDetection(value.vatCode ?? ''),
    }),
  );

  return result;
};

/**
 * "This customer is invoiced with" is a customer number in the file and the
 * internal customer id in the import mutation. Both are strings, so sending the
 * wrong one type-checks and lands a nonsense reference in the database.
 */
const resolveCustomerReference = (
  customerNo: string | null | undefined,
  customerIdByNo: Record<string, string>,
): string | null => {
  if (!customerNo) return null;
  return customerIdByNo[customerNo.trim()] ?? null;
};

const decodeDictValue = (
  value: string | null | undefined,
  array: Record<string, any>[],
): Scalars['ID']['input'] | null => {
  return Object.values(array).find(
    // A dictionary option can carry a null/empty label (the underlying DB name
    // column is nullable), so guard it before calling string methods — an
    // unguarded item.label.toUpperCase() crashed the whole import preprocessing
    // pipeline with "Cannot read properties of null (reading 'toUpperCase')"
    // (WA-3698). A null label never matches an imported value.
    (item) => item.label != null && item.label.toUpperCase().trim() == value?.toUpperCase().trim(),
  )?.id;
};

export interface IArticleImportData
  extends Omit<
    ExtendedArticleImportDataFromFile,
    | 'availabilityInProcurementKindId'
    | 'caVisibilityStatusId'
    | 'discountGroupId'
    | 'marketingGroupId'
    | 'productionGroupId'
    | 'saleUnitId'
    | 'shippingPointId'
    | 'vatCode'
  > {
  availabilityInProcurementKindId?: C_Procurement_Product_Kind | null;
  caVisibilityStatusId?: C_Ca_Visibility_Status | null;
  discountGroupId?: Scalars['ID']['input'] | null;
  marketingGroupId?: Scalars['ID']['input'] | null;
  productionGroupId?: Scalars['ID']['input'] | null;
  saleUnitId?: C_Sale_Unit | null;
  shippingPointId?: Scalars['ID']['input'] | null;
  vatCode?: C_Vat_Code | null;
}
export interface ICustomerImportData
  extends Omit<
    ExtendedCustomerImportDataFromFile,
    | 'besrBankId'
    | 'caPriceAndDiscountSourceId'
    | 'collectiveInvoicingToCustomerId'
    | 'correspondenceLanguage'
    | 'customerAddressKindId'
    | 'customerGroupId'
    | 'debitorStatus'
    | 'defaultOrderSourceId'
    | 'deliveryNoteGroupId'
    | 'directDebitBankId'
    | 'discountKindId'
    | 'eInvoiceFormat'
    | 'invoicePaymentSlipReportId'
    | 'invoiceReportId'
    | 'invoiceSummaryReportId'
    | 'invoicingIntervalId'
    | 'offerReportId'
    | 'orderReportId'
    | 'priceCategoryId'
    | 'quantityDiscountSettingId'
    | 'taxSystemId'
    | 'tourplanId'
    | 'tourplanSatId'
    | 'tourplanSunId'
    | 'transportSectorId'
  > {
  besrBankId?: Scalars['ID']['input'] | null;
  caPriceAndDiscountSourceId?: C_Discounts_And_Prices_Kind | null;
  // Resolved from the customer number in the file to the internal id the
  // import mutation takes; both are strings, so nothing else would catch it.
  collectiveInvoicingToCustomerId?: Scalars['ID']['input'] | null;
  correspondenceLanguage?: C_Correspondence_Language | null;
  // The customer number exactly as the file wrote it. Shown in the preview
  // because the resolved id means nothing to the person reading it, and because
  // a number that resolved to nothing has to be reported rather than dropped.
  collectiveInvoicingCustomerNo?: string | null;
  customerAddressKindId?: C_Customer_Address_Kind | null;
  customerGroupId?: Scalars['ID']['input'] | null;
  debitorStatus?: C_Debitor_Status | null;
  defaultOrderSourceId?: C_Customer_Def_Order_Source | null;
  deliveryNoteGroupId?: Scalars['ID']['input'] | null;
  directDebitBankId?: C_Direct_Debit_Banks | null;
  discountKindId?: C_Discount_Kind | null;
  eInvoiceFormat?: C_E_Invoice_Format | null;
  invoicePaymentSlipReportId?: C_Report | null;
  invoiceReportId?: C_Report | null;
  invoiceSummaryReportId?: C_Report | null;
  invoicingIntervalId?: C_Invoice_Interval | null;
  offerReportId?: C_Report | null;
  orderReportId?: C_Report | null;
  priceCategoryId?: Scalars['ID']['input'] | null;
  quantityDiscountSettingId?: Scalars['ID']['input'] | null;
  taxSystemId?: C_Tax_System | null;
  tourplanId?: Scalars['ID']['input'] | null;
  tourplanSatId?: Scalars['ID']['input'] | null;
  tourplanSunId?: Scalars['ID']['input'] | null;
  transportSectorId?: Scalars['ID']['input'] | null;
}

export interface IImportData
  extends Omit<
    ExtendedListImportDataFromFileQueryRes,
    'articleImportData' | 'customerImportData' | 'articleSpecialPriceImportData'
  > {
  articleImportData: IArticleImportData[];
  customerImportData: ICustomerImportData[];
  articleSpecialPriceImportData: ISpecialPriceImportData[];
}

export type ISpecialPriceImportData = ExtendedArticleSpecialPriceImportDataFromFile;
